// Asset loader with manifest-driven GLTF + procedural fallback.
//
// Workflow:
//   1. Place GLTF files under /assets/<kind>/<name>.glb
//   2. Add an entry to /assets/manifest.json:
//        { "boulder": ["assets/boulder/big-1.glb", "assets/boulder/big-2.glb"] }
//   3. The world calls `loadOrFallback("boulder", proceduralFn)`. If a GLTF
//      is registered for that kind, it's loaded and instanced; otherwise the
//      procedural function is called.
//
// This means assets land incrementally without breaking the game.

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';

const draco = new DRACOLoader();
draco.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');

const loader = new GLTFLoader();
loader.setDRACOLoader(draco);

const cache = new Map();   // url → Promise<GLTF.scene>
const manifest = { kinds: {} };
let manifestLoaded = false;

export async function loadManifest(url = 'assets/manifest.json') {
  if (manifestLoaded) return manifest;
  try {
    const res = await fetch(url);
    if (res.ok) Object.assign(manifest, await res.json());
  } catch { /* no manifest = all procedural */ }
  manifestLoaded = true;
  return manifest;
}

async function loadGLTF(url) {
  if (cache.has(url)) return cache.get(url);
  const p = new Promise((resolve, reject) => {
    loader.load(url, g => resolve(g.scene), undefined, reject);
  });
  cache.set(url, p);
  return p;
}

// Pick a random asset URL for the given kind, or null if none registered.
function pickAsset(kind, rng = Math.random) {
  const list = manifest.kinds?.[kind];
  if (!list || !list.length) return null;
  return list[Math.floor(rng() * list.length)];
}

// Returns a fresh THREE.Object3D for the given prop kind. Tries GLTF first,
// falls back to procedural. Either way, the returned object has its
// userData.kind set so downstream collision/hide logic stays uniform.
export async function makeProp(kind, proceduralFn, opts = {}) {
  const url = pickAsset(kind, opts.rng);
  if (url) {
    try {
      const tpl = await loadGLTF(url);
      const inst = tpl.clone(true);
      inst.userData.kind = kind;
      inst.userData.collideRadius = opts.collideRadius ?? 1.0;
      if (opts.hideRadius) inst.userData.hideRadius = opts.hideRadius;
      return inst;
    } catch (e) {
      console.warn(`[assets] failed to load ${url}, falling back to procedural`, e);
    }
  }
  return proceduralFn();
}
